import type { Question } from '@/features/jobs/questions/questions'
import type {
  ComputeLegendVersionRequest,
  ComputeLegendVersionResultResource,
  IntakeQuestionResource,
} from '@/features/legends/api/resources/engine'
import { mapRoutingContextSourceResourceToSource } from '@/features/legends/data-sources/data-source-mappers'
import { Types } from '@/features/legends/editor/ryno-types'
import { mapValueTypeResourceToValueType } from '@/features/legends/editor/value-type-mappers'
import { maybeParseDate } from '@/utils/date-util'
import { clamp } from '@/utils/math-util'
import type {
  LegendComputation,
  LegendComputationInput,
} from './legend-computation'

/**
 * Maps the sample input to the engine's compute request.
 *
 * @param input
 */
export function mapLegendComputationInputToRequest(
  input: LegendComputationInput,
): ComputeLegendVersionRequest {
  return {
    work_request: {
      date_submitted: input.dateSubmitted.toISOString(),
      intake_responses: input.intakeResponses,
      personal_organizer_collected_values: input.collectedValues.map((v) => ({
        collect_binding_id: v.bindingId,
        value: v.value,
      })),
    },
    skills: input.skills,
    routing_context_data_source_inputs: input.dataSourceInputs,
  }
}

/**
 * Maps the engine's compute result.
 *
 * @param resource
 */
export function mapComputeLegendVersionResultResourceToComputation(
  resource: ComputeLegendVersionResultResource,
): LegendComputation {
  return {
    questions: (resource.intake_questions ?? []).map(
      mapIntakeQuestionResourceToQuestion,
    ),
    infoLines: resource.info_lines ?? [],
    inventoryItems: (resource.inventory_items ?? []).map((item) => ({
      id: item.id,
      quantity: item.quantity,
      courtesyMultiplier: item.courtesyMultiplier ?? null,
    })),
    specs: resource.specs ?? [],
    routingMatch: {
      matchesRequirements: resource.match_inspection.matches_requirements,
      plans: resource.match_inspection.plan_matches,
    },
    organizerPrefill: (resource.organizer_prefill?.personal ?? []).map(
      (entry) => ({ bindingId: entry.prefillBindingId, value: entry.value }),
    ),
    routingContextDataSources: (
      resource.routing_context_data_sources ?? []
    ).map(mapRoutingContextSourceResourceToSource),
    variables: (resource.variables ?? []).map((variable) => ({
      id: variable.id,
      label: variable.label ?? '',
      value: variable.value,
      valueType: Types.fromJS(
        mapValueTypeResourceToValueType(variable.valueType),
      ),
    })),
    deadline: maybeParseDate(resource.deadline) ?? null,
    title: resource.title ?? null,
  }
}

/**
 * Number formatting throws past 100 decimals, which an invalid draft can ask
 * for.
 */
const maxDecimalPlaces = 100

/**
 * Maps an intake question the engine asks, without its answer: the preview
 * answers with its own responses.
 *
 * @param resource
 */
function mapIntakeQuestionResourceToQuestion(
  resource: IntakeQuestionResource,
): Question {
  const props = {
    id: resource.id,
    title: resource.question ?? '',
    description: resource.description,
    answer: null,
  }
  switch (resource.type) {
    case 'BOOL':
      return { ...props, type: 'Bool' }
    case 'SELECT':
      return {
        ...props,
        type: 'Select',
        multi: !!resource.multi,
        options: resource.options.map((o) => ({ id: o.id, text: o.text })),
      }
    case 'TEXT':
      return { ...props, type: 'Text' }
    case 'NUMERIC':
      return {
        ...props,
        type: 'Numeric',
        format:
          resource.format?.type === 'Decimal'
            ? {
                type: 'Decimal',
                places: clamp(resource.format.places ?? 0, 0, maxDecimalPlaces),
              }
            : { type: 'Integer' },
      }
    case 'DATE_TIME':
      return {
        ...props,
        type: 'DateTime',
        collectTime: !!resource.collect_time,
        disallowPast: !!resource.disallow_past,
        disallowFuture: !!resource.disallow_future,
      }
    case 'DOCUMENTS':
      return {
        ...props,
        type: 'Documents',
        options: resource.options.map((o) => ({
          id: o.id,
          text: o.text,
          documentTagIds: o.document_tags,
        })),
      }
    case 'BREAKER':
      return { ...props, type: 'Breaker' }
  }
}
